import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateAge } from '../utils/ageCalculator';
import AvatarDisplay from './AvatarDisplay';

interface Student {
  uid: string;
  name: string;
  nickname: string;
  instrument: string;
  birthdate?: string;
  planStatus: 'active' | 'expired';
  avatarUrl?: string;
  teacherAssignedBadges?: string[];
}

const TeacherAwards: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Badge definitions (same as in Awards.tsx)
  const badgeDefinitions: Record<string, { emoji: string; name: string; description: string; color: string; category: string }> = {
    'performance-ready': { emoji: '🎭', name: 'Performance Ready', description: 'Teacher says you\'re ready to perform!', color: 'from-rose-500 to-pink-600', category: 'Teacher Assigned' },
    'breakthrough-moment': { emoji: '💡', name: 'Breakthrough Moment', description: 'Teacher recognised you had a major breakthrough', color: 'from-yellow-400 to-orange-500', category: 'Teacher Assigned' },
    'technique-perfectionist': { emoji: '✨', name: 'Technique Perfectionist', description: 'Teacher praised your technique work', color: 'from-blue-400 to-indigo-500', category: 'Teacher Assigned' },
    'musical-expression': { emoji: '🎨', name: 'Musical Expression', description: 'Teacher loved your musical interpretation', color: 'from-purple-400 to-pink-500', category: 'Teacher Assigned' },
    'sight-reading-star': { emoji: '👁️', name: 'Sight Reading Star', description: 'Teacher impressed by your sight reading', color: 'from-green-400 to-teal-500', category: 'Teacher Assigned' },
    'memory-master': { emoji: '🧠', name: 'Memory Master', description: 'Teacher praised your memorisation skills', color: 'from-violet-400 to-purple-500', category: 'Teacher Assigned' },
    'rhythm-king': { emoji: '🥁', name: 'Rhythm King/Queen', description: 'Teacher loved your rhythm work', color: 'from-red-400 to-pink-500', category: 'Teacher Assigned' },
    'practice-champion': { emoji: '🏅', name: 'Practice Champion', description: 'Teacher noticed your excellent practice habits', color: 'from-amber-400 to-yellow-500', category: 'Teacher Assigned' },
    'creative-genius': { emoji: '🎪', name: 'Creative Genius', description: 'Teacher praised your creativity', color: 'from-pink-400 to-rose-500', category: 'Teacher Assigned' },
    'dedication-award': { emoji: '💎', name: 'Dedication Award', description: 'Teacher recognised your dedication', color: 'from-indigo-400 to-blue-500', category: 'Teacher Assigned' },
    'improvement-star': { emoji: '⭐', name: 'Improvement Star', description: 'Teacher noticed significant improvement', color: 'from-emerald-400 to-green-500', category: 'Teacher Assigned' },
    'lesson-hero': { emoji: '🦸', name: 'Lesson Hero', description: 'Teacher said you had an amazing lesson', color: 'from-orange-400 to-red-500', category: 'Teacher Assigned' },

    // String Technique Badges - Bow Technique
    'bow-hold-beginner': { emoji: '🤏', name: 'Bow Hold Beginner', description: 'You are learning a correct bow hold', color: 'from-amber-400 to-yellow-500', category: 'String Technique' },
    'bow-hold-developing': { emoji: '✊', name: 'Bow Hold Developing', description: 'You now have a stable bow hold', color: 'from-yellow-400 to-orange-500', category: 'String Technique' },
    'bow-hold-master': { emoji: '👑', name: 'Bow Hold Master', description: 'You consistently play with an excellent bow hold', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    
    'straight-bow-beginner': { emoji: '📏', name: 'Straight Bow Beginner', description: 'You are learning to keep the bow straight', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'straight-bow-developing': { emoji: '📐', name: 'Straight Bow Developing', description: 'You now understand how to keep the bow straight', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    'straight-bow-champion': { emoji: '🏆', name: 'Straight Bow Champion', description: 'You consistently play with a straight bow', color: 'from-purple-400 to-pink-500', category: 'String Technique' },
    
    'spiccato-beginner': { emoji: '⚡', name: 'Spiccato Beginner', description: 'You are beginning to learn spiccato', color: 'from-green-400 to-teal-500', category: 'String Technique' },
    'spiccato-developing': { emoji: '💫', name: 'Spiccato Developing', description: 'You now understand how to bounce the bow clearly', color: 'from-teal-400 to-cyan-500', category: 'String Technique' },
    'spiccato-specialist': { emoji: '⭐', name: 'Spiccato Specialist', description: 'You consistently play controlled spiccato', color: 'from-cyan-400 to-blue-500', category: 'String Technique' },
    
    'staccato-beginner': { emoji: '🔸', name: 'Staccato Beginner', description: 'You are learning short, separated strokes', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    'staccato-developing': { emoji: '🔹', name: 'Staccato Developing', description: 'You now understand how to play staccato cleanly', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    'staccato-star': { emoji: '✨', name: 'Staccato Star', description: 'You consistently play crisp staccato', color: 'from-rose-400 to-red-500', category: 'String Technique' },
    
    'legato-beginner': { emoji: '🌊', name: 'Legato Beginner', description: 'You are learning smooth connected bowing', color: 'from-violet-400 to-purple-500', category: 'String Technique' },
    'legato-developing': { emoji: '🎵', name: 'Legato Developing', description: 'You now have flowing legato phrases', color: 'from-purple-400 to-indigo-500', category: 'String Technique' },
    'legato-legend': { emoji: '🎼', name: 'Legato Legend', description: 'You consistently play beautiful legato', color: 'from-indigo-400 to-blue-500', category: 'String Technique' },
    
    'bow-distribution-beginner': { emoji: '⚖️', name: 'Bow Distribution Beginner', description: 'You are learning how to share bow evenly', color: 'from-emerald-400 to-green-500', category: 'String Technique' },
    'bow-distribution-developing': { emoji: '🎯', name: 'Bow Distribution Developing', description: 'You now understand bow planning', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'bow-distribution-pro': { emoji: '🏅', name: 'Bow Distribution Pro', description: 'You consistently use bow distribution effectively', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },
    
    'sautille-beginner': { emoji: '🦗', name: 'Sautillé Beginner', description: 'You are exploring Sautillé strokes', color: 'from-lime-400 to-green-500', category: 'String Technique' },
    'sautille-developing': { emoji: '🦎', name: 'Sautillé Developing', description: 'You now understand how to play Sautillé', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'sautille-master': { emoji: '🦅', name: 'Sautillé Master', description: 'You consistently play controlled Sautillé', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },

    // String Technique Badges - Left Hand Technique
    'vibrato-beginner': { emoji: '🌊', name: 'Vibrato Beginner', description: 'You are starting to explore vibrato', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    'vibrato-developing': { emoji: '💫', name: 'Vibrato Developing', description: 'You now have basic vibrato control', color: 'from-rose-400 to-red-500', category: 'String Technique' },
    'vibrato-virtuoso': { emoji: '🎭', name: 'Vibrato Virtuoso', description: 'You consistently play with beautiful vibrato', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    
    'intonation-beginner': { emoji: '🎯', name: 'Intonation Beginner', description: 'You are learning to check finger accuracy', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'intonation-developing': { emoji: '📐', name: 'Intonation Developing', description: 'You now understand how to adjust intonation', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    'intonation-ace': { emoji: '🎪', name: 'Intonation Ace', description: 'You consistently play with accurate intonation', color: 'from-purple-400 to-pink-500', category: 'String Technique' },
    
    'shifting-beginner': { emoji: '🚀', name: 'Shifting Beginner', description: 'You are beginning to shift between positions', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    'shifting-developing': { emoji: '🎢', name: 'Shifting Developing', description: 'You now have smoother position changes', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    'shifting-star': { emoji: '⭐', name: 'Shifting Star', description: 'You consistently shift with accuracy and ease', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    
    'double-stops-beginner': { emoji: '🤝', name: 'Double Stops Beginner', description: 'You are learning to balance two notes', color: 'from-violet-400 to-purple-500', category: 'String Technique' },
    'double-stops-developing': { emoji: '⚖️', name: 'Double Stops Developing', description: 'You now understand how to tune double stops', color: 'from-purple-400 to-indigo-500', category: 'String Technique' },
    'double-stops-master': { emoji: '🎼', name: 'Double Stops Master', description: 'You consistently play clear, balanced double stops', color: 'from-indigo-400 to-blue-500', category: 'String Technique' },
    
    'trills-beginner': { emoji: '🦋', name: 'Trills Beginner', description: 'You are learning how to play trills', color: 'from-emerald-400 to-green-500', category: 'String Technique' },
    'trills-developing': { emoji: '🌿', name: 'Trills Developing', description: 'You now understand how to make trills even', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'trills-talent': { emoji: '🎪', name: 'Trills Talent', description: 'You consistently play fast, clear trills', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },

    // String Technique Badges - Advanced Techniques
    'harmonics-beginner': { emoji: '🔮', name: 'Harmonics Beginner', description: 'You are discovering natural harmonics', color: 'from-cyan-400 to-blue-500', category: 'String Technique' },
    'harmonics-developing': { emoji: '✨', name: 'Harmonics Developing', description: 'You now understand how to produce harmonics', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'harmonics-hero': { emoji: '🦸', name: 'Harmonics Hero', description: 'You consistently play clear harmonics', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    
    'pizzicato-beginner': { emoji: '👆', name: 'Pizzicato Beginner', description: 'You are learning how to pluck clearly', color: 'from-amber-400 to-yellow-500', category: 'String Technique' },
    'pizzicato-developing': { emoji: '🤏', name: 'Pizzicato Developing', description: 'You now understand how to project pizzicato', color: 'from-yellow-400 to-orange-500', category: 'String Technique' },
    'pizzicato-pro': { emoji: '💪', name: 'Pizzicato Pro', description: 'You consistently play strong, even pizzicato', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    
    'col-legno-beginner': { emoji: '🪵', name: 'Col Legno Beginner', description: 'You are exploring col legno strokes', color: 'from-stone-400 to-gray-500', category: 'String Technique' },
    'col-legno-developing': { emoji: '🌳', name: 'Col Legno Developing', description: 'You now understand how to play col legno', color: 'from-gray-400 to-zinc-500', category: 'String Technique' },
    'col-legno-champion': { emoji: '🏆', name: 'Col Legno Champion', description: 'You consistently use col legno effectively', color: 'from-zinc-400 to-neutral-500', category: 'String Technique' },
    
    'sul-ponticello-beginner': { emoji: '🌊', name: 'Sul Ponticello Beginner', description: 'You are exploring sul ponticello sounds', color: 'from-sky-400 to-blue-500', category: 'String Technique' },
    'sul-ponticello-developing': { emoji: '🌀', name: 'Sul Ponticello Developing', description: 'You now understand how to control sul ponticello', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'sul-ponticello-star': { emoji: '💫', name: 'Sul Ponticello Star', description: 'You consistently create a strong sul ponticello sound', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },

    // Universal Technique Badges - Musical Expression
    'dynamics-beginner': { emoji: '🔊', name: 'Dynamics Beginner', description: 'You are learning to play soft and loud with control', color: 'from-blue-400 to-indigo-500', category: 'Universal Technique' },
    'dynamics-developing': { emoji: '🎚️', name: 'Dynamics Developing', description: 'You now understand how to shape dynamics effectively', color: 'from-indigo-400 to-purple-500', category: 'Universal Technique' },
    'dynamics-expert': { emoji: '🎛️', name: 'Dynamics Expert', description: 'You consistently control dynamics with expression', color: 'from-purple-400 to-pink-500', category: 'Universal Technique' },
    
    'phrasing-beginner': { emoji: '🎵', name: 'Phrasing Beginner', description: 'You are learning to shape musical phrases', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'phrasing-developing': { emoji: '🌊', name: 'Phrasing Developing', description: 'You now understand how to connect notes into flowing phrases', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    'phrasing-pro': { emoji: '🎼', name: 'Phrasing Pro', description: 'You consistently play with expressive phrasing', color: 'from-teal-400 to-cyan-500', category: 'Universal Technique' },
    
    'articulation-beginner': { emoji: '🎯', name: 'Articulation Beginner', description: 'You are learning clear starts and stops', color: 'from-orange-400 to-red-500', category: 'Universal Technique' },
    'articulation-developing': { emoji: '🎪', name: 'Articulation Developing', description: 'You now understand how to vary articulation styles', color: 'from-red-400 to-pink-500', category: 'Universal Technique' },
    'articulation-ace': { emoji: '🏆', name: 'Articulation Ace', description: 'You consistently play with clear, accurate articulation', color: 'from-pink-400 to-rose-500', category: 'Universal Technique' },
    
    'tone-quality-beginner': { emoji: '🌟', name: 'Tone Quality Beginner', description: 'You are learning to produce a steady tone', color: 'from-yellow-400 to-orange-500', category: 'Universal Technique' },
    'tone-quality-developing': { emoji: '✨', name: 'Tone Quality Developing', description: 'You now understand how to refine and shape your tone', color: 'from-orange-400 to-red-500', category: 'Universal Technique' },
    'tone-quality-star': { emoji: '💫', name: 'Tone Quality Star', description: 'You consistently create a beautiful, resonant tone', color: 'from-red-400 to-pink-500', category: 'Universal Technique' },

    // Universal Technique Badges - Technical Foundation
    'posture-beginner': { emoji: '🧍', name: 'Posture Beginner', description: 'You are learning correct playing posture', color: 'from-emerald-400 to-green-500', category: 'Universal Technique' },
    'posture-developing': { emoji: '🚶', name: 'Posture Developing', description: 'You now understand how posture affects your playing', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'posture-perfect': { emoji: '👑', name: 'Posture Perfect', description: 'You consistently maintain excellent posture', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    
    'relaxation-beginner': { emoji: '🌱', name: 'Relaxation Beginner', description: 'You are learning to release tension while playing', color: 'from-lime-400 to-green-500', category: 'Universal Technique' },
    'relaxation-developing': { emoji: '🌿', name: 'Relaxation Developing', description: 'You now understand how to stay relaxed under pressure', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'relaxation-mastery': { emoji: '🕊️', name: 'Relaxation Mastery', description: 'You consistently play with freedom and no excess tension', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    
    'endurance-beginner': { emoji: '⏱️', name: 'Endurance Beginner', description: 'You are learning to maintain focus in practice', color: 'from-violet-400 to-purple-500', category: 'Universal Technique' },
    'endurance-developing': { emoji: '💪', name: 'Endurance Developing', description: 'You now understand how to build stamina in longer sessions', color: 'from-purple-400 to-indigo-500', category: 'Universal Technique' },
    'endurance-champion': { emoji: '🏅', name: 'Endurance Champion', description: 'You consistently sustain energy and concentration in practice', color: 'from-indigo-400 to-blue-500', category: 'Universal Technique' },

    // Special Occasion Badges
    'birthday-celebration': { emoji: '🎂', name: 'Birthday Practice Star', description: 'You practised on your birthday! What dedication!', color: 'from-pink-400 to-purple-600', category: 'Special Occasion' }
  };

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'teacher') {
      navigate('/dashboard');
      return;
    }
    
    if (studentId) {
      fetchStudent();
    }
  }, [currentUser, navigate, studentId]);

  const fetchStudent = async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      const studentDoc = await getDoc(doc(db, 'users', studentId));
      
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        setStudent({
          uid: studentId,
          ...studentData,
          teacherAssignedBadges: studentData.teacherAssignedBadges || []
        } as Student);
      } else {
        setError('Student not found');
      }
    } catch (error) {
      console.error('Error fetching student:', error);
      setError('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const toggleBadge = async (badgeId: string) => {
    if (!student || !currentUser) return;
    
    try {
      setSaving(true);
      const studentRef = doc(db, 'users', student.uid);
      const currentBadges = student.teacherAssignedBadges || [];
      
      if (currentBadges.includes(badgeId)) {
        // Remove badge
        await updateDoc(studentRef, {
          teacherAssignedBadges: arrayRemove(badgeId)
        });
        setStudent(prev => prev ? {
          ...prev,
          teacherAssignedBadges: currentBadges.filter(id => id !== badgeId)
        } : null);
      } else {
        // Add badge
        await updateDoc(studentRef, {
          teacherAssignedBadges: arrayUnion(badgeId)
        });
        setStudent(prev => prev ? {
          ...prev,
          teacherAssignedBadges: [...currentBadges, badgeId]
        } : null);
      }
    } catch (error) {
      console.error('Error updating badge:', error);
      setError('Failed to update badge');
    } finally {
      setSaving(false);
    }
  };

  const renderBadge = (badgeId: string, isAssigned: boolean) => {
    const badge = badgeDefinitions[badgeId];
    if (!badge) return null;

    return (
      <div
        key={badgeId}
        className={`${isAssigned ? 'bg-gradient-to-br' : 'bg-gray-200'} ${badge.color} ${isAssigned ? 'text-gray-900' : 'text-gray-800'} p-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 cursor-pointer group relative ${
          isAssigned ? 'opacity-100' : 'opacity-70'
        } ${saving ? 'pointer-events-none' : ''}`}
        onClick={() => toggleBadge(badgeId)}
        title={`${badge.name}: ${badge.description}`}
      >
        <div className={`text-center ${isAssigned ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'} mb-2`}>
          {badge.emoji}
        </div>
        <div className={`font-bold ${isAssigned ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'}`}>
          {badge.name}
        </div>
        <div className={`${isAssigned ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} ${isAssigned ? 'text-gray-800' : 'text-gray-700'} mt-1`}>
          {badge.description}
        </div>
        
        {/* Assigned indicator */}
        {isAssigned && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
          <div className="font-semibold">{badge.name}</div>
          <div className="text-gray-300">{badge.description}</div>
          <div className="text-gray-400 mt-1">
            {isAssigned ? 'Click to remove' : 'Click to assign'}
          </div>
        </div>
      </div>
    );
  };

  const getAssignedBadges = () => {
    return student?.teacherAssignedBadges || [];
  };

  const getUnassignedBadges = () => {
    const assigned = getAssignedBadges();
    return Object.keys(badgeDefinitions).filter(badgeId => {
      if (assigned.includes(badgeId)) return false;
      
      // Filter badges based on category and student's instrument
      const category = badgeDefinitions[badgeId]?.category;
      if (category === 'String Technique') {
        const stringInstruments = ['Violin', 'Viola', 'Cello', 'Double Bass'];
        return stringInstruments.includes(student?.instrument || '');
      }
      if (category === 'Universal Technique') {
        return true; // Available to all instruments
      }
      
      return true;
    });
  };

  const filterBadgesBySearch = (badgeIds: string[]) => {
    if (!searchTerm.trim()) return badgeIds;
    
    const searchLower = searchTerm.toLowerCase();
    return badgeIds.filter(badgeId => {
      const badge = badgeDefinitions[badgeId];
      if (!badge) return false;
      
      return badge.name.toLowerCase().includes(searchLower) ||
             badge.description.toLowerCase().includes(searchLower) ||
             badge.emoji.includes(searchLower);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading student awards...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Student Not Found</h2>
          <p className="text-gray-600 mb-4">The student you're looking for doesn't exist or you don't have access.</p>
          <button
            onClick={() => navigate('/teacher/dashboard')}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div>
              <h1 className="text-4xl font-bold text-white flex items-center">
                <span className="mr-3">🏆</span>
                Assign Awards
              </h1>
              <p className="text-white/90 mt-2 text-lg">
                Awarding badges to {student.name} ({student.nickname})
              </p>
            </div>
            <button
              onClick={() => navigate('/teacher/dashboard')}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200 backdrop-blur-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Student Info */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center space-x-4">
              <AvatarDisplay 
                user={student}
                size="lg"
                alt={`${student.name}'s avatar`}
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{student.name} ({student.nickname})</h2>
                <p className="text-gray-600">{student.instrument} • Age {student.birthdate ? calculateAge(student.birthdate) : 'Not set'}</p>
                <div className="flex items-center mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    student.planStatus === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {student.planStatus}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    {getAssignedBadges().length} badges assigned
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Badges Section */}
          {getAssignedBadges().length > 0 && (
            <div className="mb-16">
              <div className="text-center mb-10">
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl p-8 border border-green-200 shadow-lg">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center">
                    <span className="mr-3">🎉</span>
                    Assigned Badges
                  </h2>
                  <p className="text-gray-700 text-lg font-medium">
                    Click any badge to remove it
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {getAssignedBadges().map(badgeId => renderBadge(badgeId, true))}
              </div>
            </div>
          )}

          {/* Available Badges Section */}
          <div>
            <div className="text-center mb-10">
              <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-8 border border-indigo-200 shadow-lg">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center">
                  <span className="mr-3">🎯</span>
                  Available Badges
                </h2>
                <p className="text-gray-700 text-lg font-medium">
                  Click any badge to assign it to {student.nickname}
                  <span className="block mt-2 text-sm text-blue-600">
                    🎼 Universal technique badges are available for all students!
                  </span>
                  {['Violin', 'Viola', 'Cello', 'Double Bass'].includes(student.instrument) && (
                    <span className="block mt-1 text-sm text-purple-600">
                      🎻 String technique badges are also available for this student!
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50/80 to-purple-50/80 backdrop-blur-sm rounded-xl p-8 mb-8 border border-blue-200/50 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="text-3xl">💡</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">How to Assign Badges</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Click on any badge below to assign it to {student.nickname}. You can assign multiple badges 
                    and remove them at any time. These badges will appear in the student's Awards page and 
                    provide motivation for their musical journey.
                  </p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="max-w-md mx-auto">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search badges to assign..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filterBadgesBySearch(getUnassignedBadges()).map(badgeId => renderBadge(badgeId, false))}
            </div>
          </div>

          {/* Summary */}
          <div className="mt-12 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{getAssignedBadges().length}</div>
                <div className="text-sm text-gray-600">Badges Assigned</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{getUnassignedBadges().length}</div>
                <div className="text-sm text-gray-600">Available to Assign</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{Object.keys(badgeDefinitions).length}</div>
                <div className="text-sm text-gray-600">Total Badges</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TeacherAwards;
